<template>
  <div class="asset-band" :class="bandClass">
    <div v-if="!hideName" class="asset-band__heading">
      <span class="asset-band__name" :title="fullName">
        <span v-if="asset.context" class="asset-band__context">{{ asset.context }}</span>
        {{ asset.name }}
      </span>
      <span v-if="!isDonut" class="asset-band__count">{{ sensorSummary }}</span>
    </div>

    <svg
      v-if="isDonut"
      class="asset-band__donut"
      viewBox="0 0 42 42"
      role="img"
      :aria-label="chartLabel"
    >
      <circle class="asset-band__donut-track" cx="21" cy="21" :r="RADIUS" />
      <circle
        v-for="arc in arcs"
        :key="arc.key"
        class="asset-band__cell"
        :class="`asset-band__cell--${arc.status}`"
        cx="21"
        cy="21"
        :r="RADIUS"
        :stroke-dasharray="arc.dashArray"
        :stroke-dashoffset="arc.dashOffset"
      ><title>{{ arc.tooltip }}</title></circle>
      <text class="asset-band__donut-label" x="21" y="21">
        {{ sensorSummary }}
      </text>
    </svg>

    <!--
      A Barra é HTML, não SVG: texto dentro de um viewBox esticado sairia
      deformado, e a deformação mudaria com a largura do cartão. Ver
      docs/adr/0007-barra-de-sensores-em-html-com-a-sigla-dentro.md
    -->
    <div
      v-else
      class="asset-band__strip"
      role="img"
      :aria-label="chartLabel"
    >
      <span
        v-for="cell in cells"
        :key="cell.key"
        class="asset-band__cell"
        :class="`asset-band__cell--${cell.status}`"
        :title="cell.tooltip"
      >{{ cell.abbreviation }}</span>
    </div>
  </div>
</template>

<script>
import { isNeutralStatus } from '@/domain/services/generalPanel/communicationStatusPanel/panelAggregator';
import getSensorAbbreviation from '@/domain/services/generalPanel/communicationStatusPanel/sensorLabel';
import { panelCharts, DEFAULT_PANEL_CHART } from '@/domain/enums/panelChart';

// Raio escolhido para que a circunferência valha ~100, deixando cada arco em
// unidades diretamente percentuais e dispensando cálculo de ângulo.
const RADIUS = 15.915;
const CIRCUMFERENCE = 100;
const ARC_GAP = 1.4;
const QUARTER_TURN = CIRCUMFERENCE / 4;

export default {
  name: 'AssetBand',
  props: {
    asset: {
      type: Object,
      required: true,
    },
    chart: {
      type: String,
      default: DEFAULT_PANEL_CHART,
    },
    hideName: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    RADIUS,
  }),
  computed: {
    isDonut() {
      return this.chart === panelCharts.DONUT;
    },
    bandClass() {
      return [
        `asset-band--${this.asset.state}`,
        { 'asset-band--matched': this.asset.matched },
      ];
    },
    sensors() {
      return this.asset.sensors || [];
    },
    /**
     * Nome completo, usado no tooltip e na descrição acessível. Na tela a
     * instalação aparece separada e com menos peso; aqui ela é necessária para
     * desambiguar fora do contexto visual.
     */
    fullName() {
      return this.asset.context
        ? `${this.asset.context} · ${this.asset.name}`
        : this.asset.name;
    },
    /**
     * Uma célula por sensor. A largura é do grid: todas iguais, e abaixo do
     * mínimo legível a faixa quebra em outra linha em vez de espremer as
     * células. Cada uma leva a Sigla do Sensor dentro.
     */
    cells() {
      return this.sensors.map((sensor, index) => ({
        key: this.cellKey(sensor, index),
        status: sensor.status,
        abbreviation: getSensorAbbreviation(sensor.name),
        tooltip: this.sensorTooltip(sensor),
      }));
    },
    /**
     * O donut é a mesma leitura da barra, enrolada em círculo: um arco de tamanho
     * igual por sensor, para que continue sendo possível contar quantos são.
     */
    arcs() {
      const total = this.sensors.length;
      if (!total) {
        return [];
      }

      const slice = CIRCUMFERENCE / total;
      const length = Math.max(slice - ARC_GAP, 0.5);

      return this.sensors.map((sensor, index) => ({
        key: this.cellKey(sensor, index),
        status: sensor.status,
        tooltip: this.sensorTooltip(sensor),
        dashArray: `${length} ${CIRCUMFERENCE - length}`,
        // O quarto de volta põe o primeiro arco no topo, e não às 3 horas.
        dashOffset: QUARTER_TURN - (index * slice),
      }));
    },
    /** Quantos sensores estão operando, ignorando os neutros. */
    sensorSummary() {
      const total = this.sensors.length;
      const neutral = this.sensors.filter(sensor => isNeutralStatus(sensor.status)).length;
      const operating = total - neutral;

      if (!neutral) {
        return `${total}`;
      }

      return this.$t('communication-status-panel.operating-of-total', {
        operating,
        total,
      });
    },
    /**
     * O gráfico é uma imagem única para leitores de tela: descreve o ativo
     * inteiro em vez de expor N formas sem rótulo.
     */
    chartLabel() {
      const state = this.$t(`communication-status-panel.state.${this.asset.state}`);
      const sensors = this.sensors
        .map(sensor => `${sensor.name}: ${this.$t(`enums.module-status.${sensor.status}`)}`)
        .join('; ');

      return `${this.fullName} — ${state}. ${sensors}`;
    },
  },
  methods: {
    /**
     * Tooltip nativo do SVG, via <title>. Identifica o IED e seu status ao passar
     * o mouse, sem depender de biblioteca de tooltip nem de estado no componente.
     */
    sensorTooltip(sensor) {
      const status = this.$t(`enums.module-status.${sensor.status}`);
      return `${sensor.name} — ${status}`;
    },
    cellKey(sensor, index) {
      return sensor.assetModuleId || `${this.asset.key}-${index}`;
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/components/communication-status-panel-fluid"

// Os `cqw` daqui medem o GroupCard que envolve a faixa, que é o container.
.asset-band
  display: flex
  flex-direction: column
  gap: 0.15rem

  &__heading
    display: flex
    align-items: baseline
    gap: 0.3rem
    min-width: 0

  &__name
    color: var(--panel-text)
    +panel-fluid(font-size, 0.95rem, 7.2cqw, 1.7rem)
    font-weight: 600
    letter-spacing: 0.01em
    min-width: 0
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  // A instalação desambigua, mas não é o que se procura no cartão: entra menor,
  // sem peso e apagada, para o nome do ativo continuar sendo o foco.
  &__context
    color: var(--panel-text-muted)
    +panel-fluid(font-size, 0.78rem, 5.9cqw, 1.4rem)
    font-weight: 400

    &::after
      content: '·'
      margin: 0 0.25rem

  &__count
    color: var(--panel-text-muted)
    +panel-fluid(font-size, 0.8rem, 6cqw, 1.4rem)
    font-variant-numeric: tabular-nums
    margin-left: auto

  // `auto-fit` com mínimo em rem é o que garante as duas exigências ao mesmo
  // tempo: colunas de largura idêntica (1fr) e nenhuma célula abaixo do que se
  // lê a três metros — quando não cabem, passam para a linha de baixo.
  // Com suporte a `cqw`, o mínimo da célula também é uma fração do cartão:
  // cartão mais largo dá sigla maior, e não só mais colunas. Os valores fixos
  // continuam sendo o piso e o fallback.
  &__strip
    display: grid
    gap: 0.18rem
    +panel-fluid(grid-auto-rows, 1.35rem, 10.2cqw, 2.4rem)
    grid-template-columns: repeat(auto-fit, minmax(2.9rem, 1fr))
    grid-template-columns: repeat(auto-fit, minmax(clamp(2.9rem, 22cqw, 5.2rem), 1fr))
    width: 100%

  &__donut
    display: block
    +panel-fluid(height, 5.6rem, 42cqw, 10rem)
    margin: 0.1rem auto 0
    +panel-fluid(width, 5.6rem, 42cqw, 10rem)

    circle
      fill: none
      stroke-linecap: butt
      stroke-width: 7

  &__donut-track
    stroke: var(--panel-donut-track)
    stroke-width: 7

  &__donut-label
    dominant-baseline: central
    fill: var(--panel-text)
    font-size: 0.62rem
    font-variant-numeric: tabular-nums
    font-weight: 700
    text-anchor: middle

  &__cell
    // Sem transição de posição: só a cor muda entre ciclos.
    transition: fill 240ms ease-out, stroke 240ms ease-out, stroke-width 120ms ease-out

    &--active
      fill: var(--panel-sensor-active)
      stroke: var(--panel-sensor-active)
    &--inactive
      fill: var(--panel-sensor-inactive)
      stroke: var(--panel-sensor-inactive)
    &--partial-communication
      fill: var(--panel-sensor-partial-communication)
      stroke: var(--panel-sensor-partial-communication)
    &--establishing-communication
      fill: var(--panel-sensor-establishing-communication)
      stroke: var(--panel-sensor-establishing-communication)
    &--indeterminate
      fill: var(--panel-sensor-indeterminate)
      stroke: var(--panel-sensor-indeterminate)
    &--disabled
      fill: var(--panel-sensor-disabled)
      stroke: var(--panel-sensor-disabled)
    &--under-maintenance
      fill: var(--panel-sensor-under-maintenance)
      stroke: var(--panel-sensor-under-maintenance)
    &--simulation
      fill: var(--panel-sensor-simulation)
      stroke: var(--panel-sensor-simulation)

  // Na Barra a célula é um elemento de texto: cor entra como fundo, e a sigla
  // recebe a tinta calculada para aquela cor. A `gap` do grid é a fresta que
  // separa dois sensores vizinhos de mesmo status.
  &__strip &__cell
    align-items: center
    border-radius: 2px
    // Contorno por dentro, sem mexer no tamanho da célula: transparente no tema
    // escuro, escurece a borda da própria cor no claro.
    box-shadow: inset 0 0 0 1px var(--panel-cell-edge)
    display: flex
    +panel-fluid(font-size, 0.72rem, 5.4cqw, 1.3rem)
    font-weight: 700
    justify-content: center
    letter-spacing: 0.02em
    min-width: 0
    overflow: hidden
    padding: 0 0.15rem
    text-overflow: ellipsis
    transition: background-color 240ms ease-out, color 240ms ease-out
    white-space: nowrap

    &:hover
      box-shadow: inset 0 0 0 1.5px var(--panel-text)

    &--active
      background: var(--panel-sensor-active)
      color: var(--panel-ink-active)
    &--inactive
      background: var(--panel-sensor-inactive)
      color: var(--panel-ink-inactive)
    &--partial-communication
      background: var(--panel-sensor-partial-communication)
      color: var(--panel-ink-partial-communication)
    &--establishing-communication
      background: var(--panel-sensor-establishing-communication)
      color: var(--panel-ink-establishing-communication)
    &--indeterminate
      background: var(--panel-sensor-indeterminate)
      color: var(--panel-ink-indeterminate)
    &--disabled
      background: var(--panel-sensor-disabled)
      color: var(--panel-ink-disabled)
    &--under-maintenance
      background: var(--panel-sensor-under-maintenance)
      color: var(--panel-ink-under-maintenance)
    &--simulation
      background: var(--panel-sensor-simulation)
      color: var(--panel-ink-simulation)

  // No donut o traço é a própria cor do arco, então o realce de hover é de
  // espessura, não de contorno.
  &__donut &__cell:hover
    stroke-width: 9.5

  // Realce sutil: a faixa que casou, dentro de um cartão já destacado.
  &--matched
    .asset-band__name
      color: var(--panel-match)

    .asset-band__strip,
    .asset-band__donut
      outline: 1px solid var(--panel-match-outline)
      outline-offset: 1px
</style>
